import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { store } from '../store';
import { router, type RouteMatch } from '../router';
import { t } from '../i18n';

/** Root component: resolves auth/route guards and renders the auth, admin, or
 *  student-portal shell (plus the public onboarding flow). */
@customElement('cadence-app')
export class CadenceApp extends LitElement {
  @state() private collapsed = false;
  @state() private mobileOpen = false;
  // Bumped on store/router changes to force re-render.
  @state() private tick = 0;

  private unsubStore?: () => void;
  private unsubRoute?: () => void;

  static styles = css`
    /* The global box-sizing reset in theme.css does not pierce Shadow DOM, so the
       width:100% + padding panels below would otherwise overflow (horizontal scroll
       on mobile). Re-apply it within this shadow root. */
    * { box-sizing: border-box; }
    :host { display: block; }
    .shell { display: flex; min-height: 100vh; }
    .main-col { flex: 1; display: flex; flex-direction: column; min-width: 0; }
    main { flex: 1; padding: var(--space-5); max-width: 1200px; width: 100%; }
    .portal-main { flex: 1; padding: var(--space-5); max-width: 1100px; width: 100%; margin: 0 auto; }
    .not-found { text-align: center; padding: var(--space-6); color: var(--color-text-secondary); }
    .not-found h1 { color: var(--color-text-primary); }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubStore = store.subscribe(() => { this.tick++; });
    this.unsubRoute = router.subscribe(() => { this.mobileOpen = false; this.tick++; });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubStore?.();
    this.unsubRoute?.();
  }

  private redirectTo(path: string) {
    // Async to avoid mutating router state synchronously during render.
    queueMicrotask(() => router.redirect(path));
  }

  // ---- Admin shell ----

  /** Generic "page not found" — also shown when a route's feature flag is off. */
  private notFoundPage() {
    return html`<div class="not-found"><h1>${t('common.errors.notFound')}</h1><p>${t('common.errors.notFoundBody')}</p></div>`;
  }

  private renderPage(route: RouteMatch) {
    switch (route.name) {
      case 'dashboard':
        return html`<cadence-dashboard-page></cadence-dashboard-page>`;
      case 'students':
        return html`<cadence-students-page></cadence-students-page>`;
      case 'student-detail':
        return html`<cadence-student-detail-page .studentId=${route.params.id}></cadence-student-detail-page>`;
      case 'teachers':
        return html`<cadence-teachers-page></cadence-teachers-page>`;
      case 'teacher-detail':
        return html`<cadence-teacher-detail-page .teacherId=${route.params.id}></cadence-teacher-detail-page>`;
      case 'families':
        return html`<cadence-families-page></cadence-families-page>`;
      case 'accounting':
        return html`<cadence-accounting-page></cadence-accounting-page>`;
      case 'reporting':
        return html`<cadence-reporting-page></cadence-reporting-page>`;
      case 'classes':
        return html`<cadence-classes-page></cadence-classes-page>`;
      case 'class-detail':
        return html`<cadence-class-detail-page .classId=${route.params.id}></cadence-class-detail-page>`;
      case 'events':
        if (!store.featureEnabled('feature_events')) return this.notFoundPage();
        return html`<cadence-events-page></cadence-events-page>`;
      case 'event-detail':
        if (!store.featureEnabled('feature_events')) return this.notFoundPage();
        return html`<cadence-event-detail-page .eventId=${route.params.id}></cadence-event-detail-page>`;
      case 'mailing':
        if (!store.featureEnabled('feature_mailing')) return this.notFoundPage();
        return html`<cadence-mailing-page></cadence-mailing-page>`;
      case 'media':
        if (!store.featureEnabled('feature_media')) return this.notFoundPage();
        return html`<cadence-media-page></cadence-media-page>`;
      case 'irish-dance':
        if (!store.featureEnabled('module_irish_dance')) return this.notFoundPage();
        return html`<cadence-irish-dance-overview-page></cadence-irish-dance-overview-page>`;
      case 'audit':
        return html`<cadence-audit-page></cadence-audit-page>`;
      case 'settings':
        return html`<cadence-settings-page section=${route.params.section || 'general'}></cadence-settings-page>`;
      default:
        return html`<div class="not-found"><h1>${t('common.errors.notFound')}</h1><p>${t('common.errors.notFoundBody')}</p></div>`;
    }
  }

  /** Map detail routes back to their sidebar section for active highlighting. */
  private sectionFor(route: RouteMatch): string {
    const map: Record<string, string> = {
      'student-detail': 'students', 'teacher-detail': 'teachers', 'class-detail': 'classes',
      'event-detail': 'events',
    };
    return map[route.name] ?? route.name;
  }

  private renderAdminShell(route: RouteMatch) {
    return html`
      <div class="shell">
        <cadence-sidebar
          active=${this.sectionFor(route)}
          settings-section=${route.params.section || 'general'}
          ?collapsed=${this.collapsed}
          ?mobile-open=${this.mobileOpen}
          @navigate=${() => (this.mobileOpen = false)}
          @toggle-collapse=${() => (this.collapsed = !this.collapsed)}
        ></cadence-sidebar>
        <div class="main-col">
          <cadence-header @toggle-sidebar=${() => (this.mobileOpen = !this.mobileOpen)}></cadence-header>
          <main>${this.renderPage(route)}</main>
        </div>
      </div>
    `;
  }

  // ---- Portal shell ----

  private renderPortalPage(route: RouteMatch) {
    switch (route.name) {
      case 'portal-dashboard':
        return html`<cadence-portal-dashboard></cadence-portal-dashboard>`;
      case 'portal-classes':
        return html`<cadence-portal-classes></cadence-portal-classes>`;
      case 'portal-levels':
        return html`<cadence-portal-levels></cadence-portal-levels>`;
      case 'portal-payments':
        return html`<cadence-portal-payments></cadence-portal-payments>`;
      case 'portal-calendar':
        return html`<cadence-portal-calendar></cadence-portal-calendar>`;
      case 'portal-events':
        if (!store.featureEnabled('feature_events')) return this.notFoundPage();
        return html`<cadence-portal-events></cadence-portal-events>`;
      case 'portal-event-detail':
        if (!store.featureEnabled('feature_events')) return this.notFoundPage();
        return html`<cadence-portal-event-detail .eventId=${route.params.id}></cadence-portal-event-detail>`;
      case 'portal-profile':
        return html`<cadence-portal-profile></cadence-portal-profile>`;
      case 'portal-contract':
        return html`<cadence-portal-contract></cadence-portal-contract>`;
      case 'portal-gallery':
        if (!store.featureEnabled('feature_media')) return this.notFoundPage();
        return html`<cadence-portal-gallery></cadence-portal-gallery>`;
      default:
        return html`<div class="not-found"><h1>${t('common.errors.notFound')}</h1></div>`;
    }
  }

  private renderPortalShell(route: RouteMatch) {
    return html`
      <div class="main-col">
        <cadence-portal-nav active=${route.name}></cadence-portal-nav>
        <main class="portal-main">${this.renderPortalPage(route)}</main>
      </div>
    `;
  }

  /** Browser-tab title: "<School> — Administration" / "— Student portal" by surface. */
  private updateTitle(route: RouteMatch) {
    const school = store.get().school?.name || t('common.app.name');
    const suffix = route.isPortal ? ` — ${t('common.title.portal')}`
      : route.isAdmin ? ` — ${t('common.title.admin')}`
      : '';
    const title = school + suffix;
    if (document.title !== title) document.title = title;
  }

  render() {
    const { ready, user, setupComplete } = store.get();
    if (!ready) return html`<cadence-loading></cadence-loading>`;

    const route = router.current();
    const authed = user !== null;
    const isStudent = user?.user_type === 'student';
    this.updateTitle(route);

    // First-run setup wizard. Until the app is initialized, force /setup (public, no
    // auth gate). Once initialized, the wizard route is no longer reachable.
    if (!setupComplete) {
      if (route.name === 'setup') return html`<cadence-setup-wizard></cadence-setup-wizard>`;
      this.redirectTo('/setup');
      return html`<cadence-loading></cadence-loading>`;
    }
    if (route.name === 'setup') {
      this.redirectTo(authed ? (isStudent ? '/portal/dashboard' : '/admin/dashboard') : '/login');
      return html`<cadence-loading></cadence-loading>`;
    }

    // Public onboarding flow — never gated by auth.
    if (route.name === 'onboarding') {
      return html`<cadence-onboarding-page .token=${route.params.token}></cadence-onboarding-page>`;
    }

    // Root → send to the right home.
    if (route.path === '/') {
      this.redirectTo(authed ? (isStudent ? '/portal/dashboard' : '/admin/dashboard') : '/login');
      return html`<cadence-loading></cadence-loading>`;
    }

    // Token-based password reset is reachable whether or not someone is signed in.
    if (route.name === 'reset') return html`<cadence-reset-password-page></cadence-reset-password-page>`;

    if (!authed) {
      if (route.name === 'login') return html`<cadence-login-page></cadence-login-page>`;
      if (route.name === 'forgot') return html`<cadence-forgot-password-page></cadence-forgot-password-page>`;
      if (route.isPortal) return html`<cadence-portal-login-page></cadence-portal-login-page>`;
      // Any other protected route while logged out → admin login.
      this.redirectTo('/login');
      return html`<cadence-loading></cadence-loading>`;
    }

    // ---- Authenticated ----
    if (isStudent) {
      // Students live in the portal; keep them out of admin and the auth pages.
      if (route.isAdmin || route.name === 'login' || route.name === 'forgot' || route.name === 'portal-login') {
        this.redirectTo('/portal/dashboard');
        return html`<cadence-loading></cadence-loading>`;
      }
      return this.renderPortalShell(route);
    }

    // Admins/teachers: portal routes don't apply to them.
    if (route.isPortal) {
      this.redirectTo('/admin/dashboard');
      return html`<cadence-loading></cadence-loading>`;
    }
    if (route.name === 'login' || route.name === 'forgot') {
      this.redirectTo('/admin/dashboard');
      return html`<cadence-loading></cadence-loading>`;
    }

    return this.renderAdminShell(route);
  }
}
